/* Section 4 — Enterprise Pivot Hero Banner */
/* Autoplay crossfade cycling (no manual dot nav), keyboard-friendly. */

(function () {
  'use strict';

  var FADE_MS = 700;
  var TEXT_SWAP_MS = 220;

  function EnterpriseHero(root) {
    this.root = root;
    this.stage = root.querySelector('[data-ehb-stage]');
    this.headlineEl = root.querySelector('[data-ehb-headline]');
    this.badgeEl = root.querySelector('[data-ehb-badge]');
    this.badgeTextEl = root.querySelector('[data-ehb-badge-text]');
    this.badgeLogoEl = root.querySelector('[data-ehb-badge-logo]');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-ehb-slide]'));

    this.index = 0;
    this.tickTimer = null;
    this.leaveTimer = null;
    this.textTimer = null;
    this.intervalMs = parseInt(root.getAttribute('data-autoplay-ms'), 10) || 6000;
    this.inView = true;
    this.isSwapping = false;

    if (this.slides.length === 0) return;

    this.syncStage(this.slides[0]);
    this.bind();
    this.reveal();
    this.start();
  }

  // Each slide paints its own colour canvas from --ehb-light / --ehb-deep, so
  // the canvas crossfades with the photo on one timeline. The stage underneath
  // carries the deep tone: it is what shows behind the copy below 990px, where
  // the layout is recomposed into a text band above a separate photo panel.
  EnterpriseHero.prototype.syncStage = function (slide) {
    if (!this.stage || !slide) return;
    var isCutout = slide.getAttribute('data-cutout') === 'true';
    this.stage.classList.toggle('is-cutout', isCutout);
    var deep = slide.getAttribute('data-deep-color');
    if (deep) this.stage.style.backgroundColor = deep;
  };

  // Fade the banner up the first time it enters the viewport, then stop
  // observing — one transition, no scroll listener, no per-frame work.
  EnterpriseHero.prototype.reveal = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) {
      this.root.classList.add('is-revealed');
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        self.root.classList.add('is-revealed');
        io.disconnect();
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.01 });
    io.observe(this.root);
  };

  EnterpriseHero.prototype.bind = function () {
    var self = this;

    // Deliberately NO pause-on-hover. A pointer resting anywhere over a
    // full-bleed hero — which is most of the screen — froze the slideshow
    // indefinitely, which read as "it just stops playing".

    // Pause while the tab is in the background instead. Browsers clamp timers
    // in hidden tabs, so without this the first tick after returning fires
    // late and the sequence looks like it stalled, then jumped.
    this.onVisibility = function () {
      if (document.hidden) {
        self.stopTick();
      } else {
        self.start();
      }
    };
    document.addEventListener('visibilitychange', this.onVisibility);

    // Don't burn crossfades while the banner is scrolled out of view.
    if ('IntersectionObserver' in window) {
      this.viewObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          self.inView = entry.isIntersecting;
          if (self.inView && !document.hidden) self.start();
          else self.stopTick();
        });
      }, { threshold: 0 });
      this.viewObserver.observe(this.root);
    }

    var mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotion = mql.matches;
    var onMotionChange = function (e) {
      self.reducedMotion = e.matches;
      if (e.matches) self.stopTick();
      else self.start();
    };
    if (mql.addEventListener) mql.addEventListener('change', onMotionChange);
    else if (mql.addListener) mql.addListener(onMotionChange);
  };

  // A self-scheduling timeout rather than setInterval. setInterval kept firing
  // on a fixed grid regardless of what the page was doing: a tick that landed
  // while a swap was already running was dropped outright, so that slide sat
  // for two full intervals. This schedules the next tick only after the last
  // one has actually completed, so every slide gets its full dwell.
  EnterpriseHero.prototype.start = function () {
    var self = this;
    if (this.slides.length < 2 || this.reducedMotion) return;
    if (document.hidden || !this.inView) return;
    this.stopTick();
    this.tickTimer = window.setTimeout(function () {
      self.tickTimer = null;
      self.goTo(self.index + 1);
    }, this.intervalMs);
  };

  EnterpriseHero.prototype.stopTick = function () {
    if (this.tickTimer) {
      window.clearTimeout(this.tickTimer);
      this.tickTimer = null;
    }
  };

  EnterpriseHero.prototype.stop = function () {
    this.stopTick();
    if (this.leaveTimer) {
      window.clearTimeout(this.leaveTimer);
      this.leaveTimer = null;
    }
    if (this.textTimer) {
      window.clearTimeout(this.textTimer);
      this.textTimer = null;
    }
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.viewObserver) this.viewObserver.disconnect();
  };

  EnterpriseHero.prototype.goTo = function (nextIndex) {
    var count = this.slides.length;
    if (count === 0) return;

    var normalized = ((nextIndex % count) + count) % count;
    if (normalized === this.index) {
      this.start();
      return;
    }

    this.isSwapping = true;

    var previous = this.slides[this.index];
    var slide = this.slides[normalized];
    this.index = normalized;

    // The outgoing slide is held opaque UNDERNEATH while the incoming one
    // fades in on top. If both faded at once, the stage colour would show
    // through the pair mid-transition and the canvas would flash.
    if (this.leaveTimer) {
      window.clearTimeout(this.leaveTimer);
      this.leaveTimer = null;
    }
    this.slides.forEach(function (s) {
      s.classList.remove('is-active');
      s.classList.remove('is-leaving');
    });
    if (previous && previous !== slide) previous.classList.add('is-leaving');
    slide.classList.add('is-active');

    var self = this;
    this.leaveTimer = window.setTimeout(function () {
      if (previous) previous.classList.remove('is-leaving');
      self.leaveTimer = null;
    }, FADE_MS);

    // Toggled alongside the image crossfade, not after the text swap, so the
    // stage colour moves on the same timeline as the canvas above it.
    this.syncStage(slide);

    // Schedule the next slide from THIS slide change, not from the end of the
    // 220ms text swap. Measured slide-to-slide that made every dwell 4.22s
    // instead of the 4s the setting asks for.
    this.start();

    if (this.headlineEl) this.headlineEl.classList.add('is-swapping');
    if (this.badgeEl) this.badgeEl.classList.add('is-swapping');

    if (this.textTimer) window.clearTimeout(this.textTimer);
    this.textTimer = window.setTimeout(function () {
      self.textTimer = null;

      var headline = slide.getAttribute('data-headline') || '';
      var clientName = slide.getAttribute('data-client-name') || '';
      var machineModel = slide.getAttribute('data-machine-model') || '';
      var monogram = slide.getAttribute('data-monogram') || '';
      var logoUrl = slide.getAttribute('data-logo-url') || '';

      if (self.headlineEl) self.headlineEl.textContent = headline;
      if (self.badgeTextEl) self.badgeTextEl.textContent = clientName + '  |  ' + machineModel;

      if (self.badgeLogoEl) {
        if (logoUrl) {
          self.badgeLogoEl.style.backgroundImage = 'url(' + logoUrl + ')';
          self.badgeLogoEl.textContent = '';
        } else {
          self.badgeLogoEl.style.backgroundImage = '';
          self.badgeLogoEl.textContent = monogram;
        }
      }

      if (self.headlineEl) self.headlineEl.classList.remove('is-swapping');
      if (self.badgeEl) self.badgeEl.classList.remove('is-swapping');

      self.isSwapping = false;
    }, TEXT_SWAP_MS);
  };

  function init() {
    var roots = document.querySelectorAll('[data-enterprise-hero]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__ehbInstance) return;
      root.__ehbInstance = new EnterpriseHero(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector ? e.target.querySelector('[data-enterprise-hero]') : null;
    if (root && !root.__ehbInstance) {
      root.__ehbInstance = new EnterpriseHero(root);
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector ? e.target.querySelector('[data-enterprise-hero]') : null;
    if (root && root.__ehbInstance) {
      root.__ehbInstance.stop();
      root.__ehbInstance = null;
    }
  });
})();
